// ============================================================================
// UploadManagerService — Background File Upload Queue
// ============================================================================
// Singleton service that owns the chunked-upload flow so an in-progress upload
// survives the user closing the dialog or navigating to a different page.
// Replaces the inline subscribe chain that used to live on FilesComponent.
//
// Phase 1: service only — no UI yet. FilesComponent still drives its own
// upload. Phase 2 will add the persistent uploads widget. Phase 3 will switch
// FilesComponent.startUpload() to call this service.
// ============================================================================

import { Injectable } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MessageService } from 'primeng/api';
import { FilesApiService } from '../files/files-api.service';
import { FileType } from '../files/files.models';
import { SessionService } from './session.service';

export type UploadStatus =
  | 'queued'
  | 'uploading'
  | 'finalizing'
  | 'success'
  | 'error';

export interface UploadJob {
  id: string;
  fileName: string;        // original file name including extension
  displayName: string;     // user-edited name (without extension)
  fileTypeName: string;
  fileSize: number;
  bytesUploaded: number;
  progress: number;        // 0-100
  status: UploadStatus;
  errorMessage?: string;
  startedAt: Date;
}

@Injectable({ providedIn: 'root' })
export class UploadManagerService {
  private jobsSubj = new BehaviorSubject<UploadJob[]>([]);
  jobs$: Observable<UploadJob[]> = this.jobsSubj.asObservable();

  /** Emits whenever a job reaches a terminal state (success or error). */
  private completedSubj = new Subject<UploadJob>();
  jobCompleted$: Observable<UploadJob> = this.completedSubj.asObservable();

  constructor(
    private filesApi: FilesApiService,
    private session: SessionService,
    private messageSvc: MessageService
  ) {
    // Warn the user if they try to refresh/close the tab while an upload is
    // still in flight. Page reload kills the in-flight HTTP requests; the
    // browser shows its native confirm dialog when we cancel the unload event.
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  private onBeforeUnload = (event: BeforeUnloadEvent): string | void => {
    if (!this.hasActiveUploads()) return;
    event.preventDefault();
    // Modern browsers ignore the custom string, but legacy ones still read
    // returnValue. Setting it covers both paths.
    event.returnValue = '';
    return '';
  };

  private hasActiveUploads(): boolean {
    return this.jobsSubj.value.some(j =>
      j.status === 'queued' || j.status === 'uploading' || j.status === 'finalizing'
    );
  }

  /** Kick off an upload. Returns the job id so callers can correlate with toasts/UI. */
  startUpload(file: File, fileType: FileType, displayName: string): string {
    const job: UploadJob = {
      id: this.makeId(),
      fileName: file.name,
      displayName,
      fileTypeName: fileType.FT_Name,
      fileSize: file.size,
      bytesUploaded: 0,
      progress: 0,
      status: 'queued',
      startedAt: new Date()
    };
    this.addJob(job);

    const ext = this.extractExtension(file.name);
    if (Number(fileType.FT_DBStoreDefault) === 1) {
      this.runDbDirect(job, file, fileType, displayName, ext);
    } else {
      this.runTeams(job, file, fileType, displayName, ext);
    }
    return job.id;
  }

  /** Remove a finished (success or error) job from the visible queue. */
  dismissJob(id: string): void {
    this.updateJobs(jobs => jobs.filter(j => j.id !== id));
  }

  /** Remove all finished jobs at once. */
  dismissAllFinished(): void {
    this.updateJobs(jobs => jobs.filter(j => j.status === 'queued' || j.status === 'uploading' || j.status === 'finalizing'));
  }

  // -- internal flow -------------------------------------------------------

  private runDbDirect(job: UploadJob, file: File, fileType: FileType, displayName: string, ext: string): void {
    this.updateJob(job.id, { status: 'uploading' });
    const reader = new FileReader();
    reader.onload = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const base64 = btoa(binary);
      const customerId = this.session.userInfo?.customer_id || '';

      this.filesApi.saveFileToDBAsBytes(
        customerId, fileType.FT_ID, displayName, ext, base64
      ).subscribe({
        next: () => this.markSuccess(job.id),
        error: () => this.markError(job.id, 'Upload failed.')
      });
    };
    reader.onerror = () => this.markError(job.id, 'Failed to read file.');
    reader.readAsArrayBuffer(file);
  }

  private runTeams(job: UploadJob, file: File, fileType: FileType, displayName: string, ext: string): void {
    this.updateJob(job.id, { status: 'uploading' });
    const customerId = this.session.userInfo?.customer_id || '';

    this.filesApi.requestUploadUrl(
      fileType.FT_ID, fileType.FT_Name, displayName, ext, customerId
    ).subscribe({
      next: (resp: any) => {
        const data = Array.isArray(resp) ? resp[0]?.Data?.[0] || resp[0] : resp;
        if (data?.uploadUrl) {
          this.uploadChunks(job, file, ext, data.uploadUrl, data.erpId, data.fileId, data.status, fileType, displayName);
        } else if (data?.error) {
          this.markError(job.id, String(data.error));
        } else {
          this.markError(job.id, 'Failed to get upload URL.');
        }
      },
      error: () => this.markError(job.id, 'Failed to initiate upload.')
    });
  }

  private uploadChunks(
    job: UploadJob, file: File, ext: string, uploadUrl: string,
    erpId: string, fileId: string, status: number,
    fileType: FileType, displayName: string
  ): void {
    const chunkSize = 50 * 1024 * 1024;
    const fileSize = file.size;
    let start = 0;

    const next = (): void => {
      const end = Math.min(start + chunkSize, fileSize);
      const chunk = file.slice(start, end);

      this.filesApi.uploadChunkToTeams(uploadUrl, chunk, start, end, fileSize).subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            const totalLoaded = start + event.loaded;
            this.updateJob(job.id, {
              bytesUploaded: totalLoaded,
              progress: Math.round((totalLoaded / fileSize) * 100)
            });
          }
          if (event.type === HttpEventType.Response) {
            start = end;
            if (start < fileSize) {
              next();
            } else {
              this.finalize(job, ext, erpId, fileId, status, fileType, displayName);
            }
          }
        },
        error: () => this.markError(job.id, 'Chunk upload failed.')
      });
    };
    next();
  }

  private finalize(
    job: UploadJob, ext: string, erpId: string, fileId: string,
    status: number, fileType: FileType, displayName: string
  ): void {
    this.updateJob(job.id, { status: 'finalizing', progress: 100 });
    const action = (status === 1 || status === -1) ? 'UpdateFileDate' : 'AddFile';
    this.filesApi.updateOrAddFile(action, {
      customer_id: this.session.userInfo?.customer_id || '',
      filetype_id: fileType.FT_ID,
      filetype_name: fileType.FT_Name,
      filename: displayName,
      extension: ext,
      erpId,
      fileId
    }).subscribe({
      next: () => this.markSuccess(job.id),
      error: () => this.markError(job.id, 'Failed to finalize upload.')
    });
  }

  // -- state transitions ---------------------------------------------------

  private markSuccess(jobId: string): void {
    const updated = this.updateJob(jobId, { status: 'success', progress: 100 });
    if (!updated) return;
    this.messageSvc.add({
      severity: 'success',
      summary: 'Upload complete',
      detail: updated.fileName
    });
    this.completedSubj.next(updated);
  }

  private markError(jobId: string, msg: string): void {
    const updated = this.updateJob(jobId, { status: 'error', errorMessage: msg });
    if (!updated) return;
    this.messageSvc.add({
      severity: 'error',
      summary: 'Upload failed',
      detail: `${updated.fileName}: ${msg}`,
      life: 8000
    });
    this.completedSubj.next(updated);
  }

  // -- helpers -------------------------------------------------------------

  private addJob(job: UploadJob): void {
    this.jobsSubj.next([...this.jobsSubj.value, job]);
  }

  private updateJobs(fn: (jobs: UploadJob[]) => UploadJob[]): void {
    this.jobsSubj.next(fn(this.jobsSubj.value));
  }

  private updateJob(id: string, patch: Partial<UploadJob>): UploadJob | undefined {
    let found: UploadJob | undefined;
    this.updateJobs(jobs => jobs.map(j => {
      if (j.id !== id) return j;
      found = { ...j, ...patch };
      return found;
    }));
    return found;
  }

  private extractExtension(name: string): string {
    const idx = name.lastIndexOf('.');
    return idx > 0 ? name.substring(idx) : '';
  }

  private makeId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return (crypto as any).randomUUID();
    }
    return 'job-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
  }
}
